import { FC, useRef, useState, useMemo } from 'react'
import { useNotifications } from 'src/hooks/useNotifications'
import { useTranslation } from 'react-i18next'
import NotificationItem, {
  INotification,
} from 'src/components/elements/header/components/notifications/item/NotificationItem'
import {
  NotificationType,
  useDeleteNotificationMutation,
  useMarkAllAsReadMutation,
} from 'src/redux/api'
import { Skeleton } from '@mui/material'
import { EmptySection } from 'src/components/elements'
import { RadioChoice } from 'src/components/ui/choice/Choice'
import { NotificationIcon } from '@svgs'
import ErrorState from 'src/components/elements/error-state/ErrorState'

type FilterType = 'all' | 'unread' | NotificationType

const NotificationList: FC = () => {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [filter, setFilter] = useState<FilterType>('all')
  const [markAllAsRead, { isLoading: isMarkingAll }] = useMarkAllAsReadMutation()
  const [deleteNotification] = useDeleteNotificationMutation()

  const handleDismiss = (id: string, wasUnread: boolean) => {
    deleteNotification({ id, wasUnread })
  }

  const {
    notifications,
    groupedNotifications,
    formatNotificationDate,
    lastElementRef,
    observerMarkRef,
    unreadCount,
    isLoading,
    isError,
    refetch,
  } = useNotifications(20)

  // Filter notifications based on selected filter
  const filteredGroupedNotifications = useMemo(() => {
    return Object.entries(groupedNotifications).reduce(
      (acc, [date, times]) => {
        const filteredTimes = Object.entries(times).reduce(
          (timeAcc, [time, notifs]) => {
            const filtered = notifs.filter((notif) => {
              if (filter === 'all') return true
              if (filter === 'unread') return !notif.read
              return notif.type === filter
            })
            if (filtered.length > 0) {
              timeAcc[time] = filtered
            }
            return timeAcc
          },
          {} as Record<string, typeof notifications>,
        )
        if (Object.keys(filteredTimes).length > 0) {
          acc[date] = filteredTimes
        }
        return acc
      },
      {} as Record<string, Record<string, typeof notifications>>,
    )
  }, [groupedNotifications, filter, notifications])

  const filters: { value: FilterType; label: string }[] = [
    { value: 'all', label: t('notification.filters.all', 'All') },
    { value: 'unread', label: t('notification.filters.unread', 'Unread') },
    { value: 'SYSTEM', label: t('notification.filters.system', 'System') },
    { value: 'SPECIAL_OFFERS', label: t('notification.filters.special_offers', 'Special Offers') },
    { value: 'NEW_MESSAGE', label: t('notification.filters.messages', 'Messages') },
    { value: 'NEW_PROPERTY', label: t('notification.filters.new_properties', 'New Properties') },
    { value: 'PRICE_CHANGE', label: t('notification.filters.price_changes', 'Price Changes') },
  ]

  const hasNotifications = notifications.length > 0
  const hasFilteredNotifications = Object.keys(filteredGroupedNotifications).length > 0

  if (isLoading) {
    return (
      <div className="mt-2 flex flex-col gap-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: '1rem' }} />
        ))}
      </div>
    )
  }

  // a failed load used to read as "no notifications yet"
  if (isError && !hasNotifications) {
    return <ErrorState onRetry={refetch} className="mt-2" />
  }

  if (!hasNotifications) {
    return (
      <div className="mt-8">
        <EmptySection
          title={t('notification.empty.title', 'No notifications yet')}
          description={t('notification.empty.description', 'You will see your notifications here')}
          icon={<NotificationIcon />}
        />
      </div>
    )
  }

  return (
    <div className="mt-2">
      {/* Filters: the system's radio chips — one is chosen, a screen reader
          hears the group and which one (they were plain buttons) */}
      <RadioChoice
        legend={t('notification.filters.label')}
        legendClassName="sr-only"
        name="notification-filter"
        className="mb-4"
        options={filters}
        value={filters.find((option) => option.value === filter) ?? null}
        onChange={(option) => setFilter(option.value as FilterType)}
      />

      {/* Unread count badge + mark-all-as-read */}
      {unreadCount > 0 && (
        <div className="flex items-center justify-between gap-2 mb-4 text-sm text-ink-2">
          <div className="flex items-center gap-2">
            <span>
              {t('notification.unread_count', {
                count: unreadCount,
                defaultValue: `${unreadCount} unread`,
              })}
            </span>
            <div className="w-2 h-2 bg-danger-strong rounded-full" />
          </div>
          <button
            onClick={() => markAllAsRead()}
            disabled={isMarkingAll}
            className="min-h-11 font-bold text-brand-ink hover:underline disabled:opacity-50"
          >
            {t('notification.mark_all_read', 'Mark all as read')}
          </button>
        </div>
      )}

      {/* Notifications list */}
      {!hasFilteredNotifications ? (
        <div className="mt-8">
          <EmptySection
            title={t('notification.empty.no_filtered_title', 'No notifications found')}
            description={t('notification.empty.no_filtered_description', 'Try changing the filter')}
            icon={<NotificationIcon />}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4" ref={scrollRef}>
          {Object.entries(filteredGroupedNotifications).map(([date, times]) => (
            <div key={date} className="flex flex-col gap-4">
              {Object.entries(times).map(([time, notificationsAtTime]) => (
                <div key={time} className="flex flex-col gap-3">
                  {notificationsAtTime.length > 0 && (
                    <>
                      <div className="text-sm font-extrabold text-ink-2">
                        {formatNotificationDate(notificationsAtTime[0].createdAt)}
                      </div>
                      <div className="flex flex-col gap-2">
                        {notificationsAtTime.map((notification: INotification) => (
                          <div
                            key={notification.id}
                            ref={(el) => {
                              if (el && observerMarkRef.current && !notification.read) {
                                observerMarkRef.current.observe(el)
                                el.setAttribute('data-id', notification.id)
                              }
                            }}
                          >
                            <NotificationItem {...notification} onDismiss={handleDismiss} />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
          <div ref={lastElementRef} className="h-1 w-full"></div>
        </div>
      )}
    </div>
  )
}

export default NotificationList
